/**
 * Starter decks: the ladders the method climbs, ready on day one.
 *
 * CoffeePie started with capital letters, did lowercase the very next day,
 * then numbers, and later words, sight words and arithmetic, about ten cards
 * a deck a day. These are those decks, built from the phonics tables so every
 * card is spoken in Gigi's voice and pictured by the clay set: letters in the
 * order the reading engines teach them (s a t p i n first, so each letter she
 * learns is one she can blend with), words by blending level, sight words in
 * the reading path's small sets. Where reading is not the point
 * (counting, patterns, hearing a sound) the cards check themselves, so a
 * session never waits on a parent for those.
 */

import type { Card, CardFace, Deck } from './decks'
import { SIGHT_SETS, SOUND_SETS } from './books'
import { GRAPHEMES, WORDS, WORD_BY_TEXT } from './phonics'

/**
 * Letters in teaching order: the reading path's sound sets first (m s a f,
 * then l r n o ...), so the cards and the path teach the same next sound,
 * then the letters no set has yet.
 */
const LETTER_ORDER = [...new Set([...SOUND_SETS.flatMap((s) => s.letters), ...'satpinmdgockeurhbfljvwxyzq'.split('')])]

/**
 * The picture that goes with a letter's sound: a word-bank word that starts
 * with it, chosen by hand where several do (the clearest clay figure wins).
 * A few sounds have no pictured word; those get an emoji and no word, so the
 * card never names a word the voice has no clip for ("insect").
 */
const LETTER_PICTURE: Record<string, CardFace> = {
  s: { picture: 'sun' }, a: { picture: 'ant' }, t: { picture: 'tent' }, p: { picture: 'pig' },
  i: { emoji: '🐞' }, n: { picture: 'nest' }, m: { picture: 'moon' }, d: { picture: 'dog' },
  g: { picture: 'goat' }, o: { picture: 'ox' }, c: { picture: 'cat' }, k: { picture: 'king' },
  e: { picture: 'egg' }, u: { emoji: '⬆️', text: 'up', say: 'w:up' }, r: { picture: 'rock' }, h: { picture: 'hat' },
  b: { picture: 'bus' }, f: { picture: 'fox' }, l: { picture: 'leg' }, j: { picture: 'jet' },
  v: { picture: 'van' }, w: { picture: 'web' }, x: { picture: 'box' }, y: { emoji: '🪀' },
  z: { picture: 'zip' }, q: { emoji: '👑' },
}

/** A pictured word as the back of a card: the clay figure, the word under it, and its voice. */
function wordBack(word: string): CardFace {
  return { picture: word, text: word, say: `w:${word}` }
}

function letterCard(letter: string, shown: string): Card {
  const sound = letter === 'q' ? 'kw' : letter === 'x' ? 'ks' : GRAPHEMES[letter]!
  const pic = LETTER_PICTURE[letter]!
  const back: CardFace = pic.picture ? wordBack(pic.picture) : pic
  return { id: letter, kind: 'read', front: { text: shown, say: `p:${sound}` }, back }
}

const capitals: Deck = {
  id: 'capitals',
  title: 'Big letters',
  direction: 'Capital letters and the sound each one makes',
  cover: 'ABC',
  daily: 10,
  maxNew: 3,
  cards: LETTER_ORDER.map((l) => letterCard(l, l.toUpperCase())),
}

const lowercase: Deck = {
  id: 'lowercase',
  title: 'Letter sounds',
  direction: 'The sound each little letter makes (mmm, not em), a few at a time in the order her reading path teaches them',
  cover: 'abc',
  daily: 10,
  maxNew: 3,
  cards: LETTER_ORDER.map((l) => letterCard(l, l)),
}

export const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten',
  'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen', 'twenty']

const gen = (id: string, type: NonNullable<Card['gen']>['type'], params?: Record<string, unknown>): Card =>
  ({ id, kind: 'generator', front: {}, gen: params ? { type, params } : { type } })

const numberCard = (n: number): Card => ({ id: `n${n}`, kind: 'read', front: { text: String(n), say: `w:${NUMBER_WORDS[n]}` } })

/** 1 to 20 to read (the back shows that many dots), then what comes after and before, once she has the numbers for it. */
const numbers: Deck = {
  id: 'numbers',
  title: 'Numbers',
  direction: 'Numbers 1 to 20, then what comes after and before',
  cover: '123',
  daily: 10,
  maxNew: 3,
  cards: [
    ...Array.from({ length: 10 }, (_, i) => numberCard(i + 1)),
    gen('after-10', 'after', { max: 10 }),
    gen('before-10', 'before', { max: 10 }),
    ...Array.from({ length: 10 }, (_, i) => numberCard(i + 11)),
    gen('after-20', 'after', { max: 20 }),
    gen('before-20', 'before', { max: 20 }),
  ],
}

/**
 * Two letters, one sound: read aloud, with the word it lives in on the back.
 * After each group of look-alikes she hears one and finds it among them, so
 * sh, ch and th are told apart by ear as well as read.
 */
const TEAMS: Array<[string, string]> = [
  ['sh', 'ship'], ['ch', 'chick'], ['th', 'bath'], ['ck', 'duck'], ['ng', 'ring'],
  ['ee', 'tree'], ['oo', 'moon'], ['ai', 'rain'], ['oa', 'boat'], ['ar', 'star'], ['or', 'fork'], ['igh', 'night'], ['ow', 'cow'],
]

/**
 * The two-letter sounds in small sets, after the single letter sounds: the
 * ones blending level 3 reads (sh, ch, th, ck, ng), then the vowel teams of
 * level 5. Her decks bring them in after SOUND_SETS, by the same rule.
 */
export const TEAM_SETS: string[][] = [['sh', 'ch', 'th'], ['ck', 'ng'], ['ee', 'oo', 'ai'], ['oa', 'ar', 'or'], ['igh', 'ow']]
function hear(id: string, answer: string, others: string[]): Card {
  return { id, kind: 'listen', front: { text: answer, say: `p:${GRAPHEMES[answer]}` }, options: others.map((text) => ({ text })) }
}
const sounds: Deck = {
  id: 'sounds',
  title: 'Two-letter sounds',
  direction: 'Two letters that make one sound: sh, ch, th, ee, oo and friends. They come in once her single letter sounds are in',
  cover: 'ship',
  daily: 10,
  maxNew: 2,
  cards: TEAMS.flatMap(([g, word]): Card[] => {
    const read: Card = { id: g, kind: 'read', front: { text: g, say: `p:${GRAPHEMES[g]}` }, back: wordBack(word) }
    if (g === 'th') return [read, hear('hear-sh', 'sh', ['ch', 'th']), hear('hear-ch', 'ch', ['sh', 'th'])]
    if (g === 'oo') return [read, hear('hear-ee', 'ee', ['oo', 'sh'])]
    if (g === 'ar') return [read, hear('hear-oo', 'oo', ['ee', 'ar'])]
    return [read]
  }),
}

/**
 * Words to sound out, easiest first: held first sounds, then quick ones, then
 * two letters making one sound. Every fifth card she reads a word and picks
 * its picture, a card that checks itself and shows she read it for meaning.
 */
const firstWords: Deck = (() => {
  const words = WORDS.filter((w) => w.level <= 3)
  const cards: Card[] = []
  words.forEach((w, i) => {
    // The front's voice is said only after the parent grades: before that the word is hers to read.
    cards.push({ id: w.word, kind: 'read', front: { text: w.word, say: `w:${w.word}` }, back: wordBack(w.word) })
    if (i % 5 === 4) {
      // Two other pictures from her level that start differently, so the first letter alone does not give it away.
      const others = words.filter((o) => o.level === w.level && o.word !== w.word && o.g[0] !== w.g[0] && o.g.at(-1) !== w.g.at(-1))
      const pick = [others[(i * 7) % others.length]!, others[(i * 13 + 3) % others.length]!].filter((o, k, a) => a.indexOf(o) === k)
      if (pick.length === 2) cards.push({ id: `pick-${w.word}`, kind: 'choose', front: { text: w.word }, back: wordBack(w.word), options: pick.map((o) => ({ picture: o.word })) })
    }
  })
  return { id: 'first-words', title: 'First words', direction: 'Short words she can sound out, with a picture for each', cover: 'cat', daily: 10, maxNew: 3, cards }
})()

/**
 * The sight words in the reading path's sets and order (the order the little
 * books need them), so the deck and the path teach the same words and count
 * the same total.
 */
const sightWords: Deck = {
  id: 'sight-words',
  title: 'Sight words',
  direction: `Words to know on sight, a few at a time in the order her books need them: ${SIGHT_SETS.slice(0, 3).map((set) => set.join(' ')).join(', then ')}, and so on`,
  cover: '👀',
  daily: 10,
  maxNew: 2,
  cards: SIGHT_SETS.flat().map((w) => ({ id: w.toLowerCase(), kind: 'read' as const, front: { text: w, say: `w:${w.toLowerCase()}` } })),
}

/**
 * Counting to 5, then to 10; adding and taking away come in only once she
 * counts (its steps), since at her age they rest on counting.
 */
const math: Deck = {
  id: 'math',
  title: 'Counting',
  direction: 'Counting clay things to 5, then to 10; adding and taking away once she counts',
  cover: '🧮',
  daily: 8,
  maxNew: 2,
  cards: [
    gen('count-5', 'count', { max: 5 }),
    gen('count-10', 'count', { max: 10 }),
    gen('add-5', 'add', { max: 5 }),
    gen('take-5', 'subtract', { max: 5 }),
    gen('add-10', 'add', { max: 10 }),
    gen('take-10', 'subtract', { max: 10 }),
  ],
  steps: [['count-5'], ['count-10'], ['add-5', 'take-5'], ['add-10', 'take-10']],
}

/** The kind of puzzle gifted tests ask: what comes next, from two colors to three-part rules. */
const patterns: Deck = {
  id: 'patterns',
  title: 'Patterns',
  direction: 'What comes next: colors, shapes, animals and letters in repeating patterns',
  cover: '🔷',
  daily: 6,
  maxNew: 2,
  cards: [
    gen('ab-colors', 'pattern', { rule: 'AB', set: 'colors' }),
    gen('ab-shapes', 'pattern', { rule: 'AB', set: 'shapes' }),
    gen('ab-animals', 'pattern', { rule: 'AB', set: 'animals' }),
    gen('aab', 'pattern', { rule: 'AAB', set: 'colors' }),
    gen('abb', 'pattern', { rule: 'ABB', set: 'shapes' }),
    gen('abc', 'pattern', { rule: 'ABC', set: 'animals' }),
    gen('aabb', 'pattern', { rule: 'AABB', set: 'both' }),
    gen('ab-letters', 'pattern', { rule: 'AB', set: 'letters' }),
    gen('rhyme', 'rhyme'),
  ],
}

/**
 * Every starter deck, in the order the method reaches for them: capitals,
 * lowercase the day after, numbers, then the rest as she is ready.
 */
export const STARTER_DECKS: Deck[] = [capitals, lowercase, numbers, sounds, firstWords, sightWords, math, patterns]

/**
 * A starter deck by its id or by its title, which is what a parent (and the
 * agent) calls it: "Letter sounds" is the single letters whatever its id.
 */
export function starterDeck(idOrTitle: string): Deck | undefined {
  const name = idOrTitle.trim().toLowerCase()
  return STARTER_DECKS.find((d) => d.title.toLowerCase() === name) ?? STARTER_DECKS.find((d) => d.id === name)
}

/** For tests and parents: every picture word a starter deck names is in the word bank. */
export function starterPictureWords(): string[] {
  const out = new Set<string>()
  const add = (f?: CardFace) => { if (f?.picture) out.add(f.picture) }
  for (const d of STARTER_DECKS) {
    if (d.cover && WORD_BY_TEXT[d.cover]) out.add(d.cover)
    for (const c of d.cards) { add(c.front); add(c.back); c.options?.forEach(add) }
  }
  return [...out]
}
